import { useQuery } from '@apollo/client';
import { PeopleOutlined } from '@mui/icons-material';
import { Box } from '@mui/material';
import { useFormik } from 'formik';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { object, string } from 'yup';
import { useDeleteUser, useUpdateUser } from '~/api/users';
import { RouterBreadcrumbs } from '~/components/RouterBreadcrumbs';
import { ScrollableRouterTabs } from '~/components/RouterTabs';
import { Tooltip } from '~/components/Tooltip';
import { DeleteButton, SaveButton } from '~/components/button';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { DetailTextField } from '~/components/forms/details';
import { LanguageSelect } from '~/components/inputs';
import { LoadingPane } from '~/components/loading-pane';
import { PageContainer } from '~/components/page-layout';
import {
  SettingDescription,
  SettingInputWrapper,
  SettingLabelWrapper,
  SettingName,
  SettingsBody,
  SettingsData,
  SettingsHead,
} from '~/components/settings';
import { Timestamps } from '~/components/timestamps';
import { Toolbar } from '~/components/toolbar';
import { useAppContext } from '~/contexts';
import { useConfirmDialog } from '~/hooks/dialogs';
import { useLink } from '~/hooks/link';
import { formatDate } from '~/lib/datetime';
import { NetworkUserSettingsDocument } from '../queries/settings.generated';
import { tabs } from './lib/tabs';

const validationSchema = object({
  locale: string().nullable(),
  name: string().required(),
});

export const UserSettings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { currentNetwork, currentUser } = useAppContext();

  const params = useParams<{ userId: string }>();

  const userId = Number(params.userId);

  const { data, loading } = useQuery(NetworkUserSettingsDocument, {
    variables: { networkId: currentNetwork.id, userId },
  });

  const [deleteUser] = useDeleteUser();
  const [updateUser] = useUpdateUser();

  const [confirm, confirmProps] = useConfirmDialog();

  const link = useLink();

  const networkMembership = data?.network?.networkMembership;
  const user = networkMembership?.user;

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      locale: user?.locale ?? '',
      name: user?.name ?? '',
    },
    onSubmit: async (values) => {
      await updateUser({
        variables: {
          userId,
          patch: {
            ...validationSchema.cast(values),
            locale: values.locale || null,
          },
        },
      });
    },
    validateOnBlur: true,
    validationSchema,
  });

  const handleDelete = useCallback(async () => {
    if (!(await confirm())) return;
    const { data } = await deleteUser({
      variables: { input: { networkId: currentNetwork.id, userId } },
    });
    if (data?.deleteUser?.user.id) navigate(link('/users'), { replace: true });
  }, [confirm, currentNetwork.id, deleteUser, link, navigate, userId]);

  return (
    <LoadingPane in={loading && !data} size={80} thickness={4}>
      <>
        <Toolbar
          actions={
            <Box sx={{ display: 'flex', gap: 2 }}>
              <SaveButton
                disabled={formik.isSubmitting || !formik.isValid || !formik.dirty}
                type="submit"
                form="user-settings-form"
              />
              <Tooltip
                title={
                  networkMembership?.canDestroyUser.value
                    ? ''
                    : networkMembership?.canDestroyUser.reasons?.fullMessages[0] ??
                      t('pages.users.detail.settings.no_permission_delete')
                }
              >
                <span>
                  <DeleteButton disabled={!user?.canDestroy.value} onClick={handleDelete} />
                </span>
              </Tooltip>
            </Box>
          }
          breadcrumbsLabel={<RouterBreadcrumbs />}
          titleIcon={<PeopleOutlined />}
          titleText={user?.name}
        />

        <ScrollableRouterTabs current={t('common.labels.details')} tabs={tabs(t)} />

        <PageContainer>
          <Box
            component="form"
            id="user-settings-form"
            onSubmit={formik.handleSubmit}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <div>
              <SettingsHead>{t('common.labels.general')}</SettingsHead>
              <SettingsBody>
                <SettingsData>
                  <SettingLabelWrapper>
                    <SettingName>{t('common.labels.name')}</SettingName>
                    <SettingDescription>{t('common.labels.name_description')}</SettingDescription>
                  </SettingLabelWrapper>
                  <SettingInputWrapper>
                    <DetailTextField
                      autoFocus
                      error={!!formik.errors.name}
                      fullWidth
                      helperText={formik.errors.name ?? ' '}
                      name="name"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      value={formik.values.name}
                    />
                  </SettingInputWrapper>
                </SettingsData>
                <SettingsData>
                  <SettingLabelWrapper>
                    <SettingName>{t('common.labels.email')}</SettingName>
                    <SettingDescription>{t('common.labels.email_description')}</SettingDescription>
                  </SettingLabelWrapper>
                  <SettingInputWrapper>
                    <DetailTextField disabled fullWidth value={user?.email ?? ''} />
                  </SettingInputWrapper>
                </SettingsData>
                <SettingsData>
                  <SettingLabelWrapper>
                    <SettingName>
                      {t('pages.users.detail.settings.authentication_type')}
                    </SettingName>
                    <SettingDescription>
                      {t('pages.users.detail.settings.authentication_type_description')}
                    </SettingDescription>
                  </SettingLabelWrapper>
                  <SettingInputWrapper>
                    <DetailTextField disabled fullWidth value={user?.authStrategy ?? ''} />
                  </SettingInputWrapper>
                </SettingsData>
                <SettingsData className="last">
                  <SettingLabelWrapper>
                    <SettingName>{t('pages.users.detail.settings.roles')}</SettingName>
                    <SettingDescription>
                      {t('pages.users.detail.settings.roles_description')}
                    </SettingDescription>
                  </SettingLabelWrapper>
                  <SettingInputWrapper>
                    <DetailTextField
                      disabled
                      fullWidth
                      value={user?.roles.map((role) => role.name).join(', ')}
                    />
                  </SettingInputWrapper>
                </SettingsData>
              </SettingsBody>
            </div>

            {currentNetwork.settings.enableLanguageSelection && (
              <div>
                <SettingsHead>{t('pages.users.detail.settings.preferences_heading')}</SettingsHead>
                <SettingsBody>
                  <SettingsData className="last">
                    <SettingLabelWrapper>
                      <SettingName>{t('pages.users.detail.settings.language')}</SettingName>
                      <SettingDescription>
                        {t('pages.users.detail.settings.language_description')}
                      </SettingDescription>
                    </SettingLabelWrapper>
                    <SettingInputWrapper>
                      <LanguageSelect
                        name="locale"
                        value={formik.values.locale}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        browserDefaultLabel={t(
                          'pages.users.detail.settings.language_browser_default',
                        )}
                      />
                    </SettingInputWrapper>
                  </SettingsData>
                </SettingsBody>
              </div>
            )}

            {currentUser.admin && (
              <Timestamps createdAt={user?.createdAt ?? ''} updatedAt={user?.updatedAt ?? ''} />
            )}
            <div>
              <SettingsHead>
                {t('pages.users.detail.settings.sign_in_details_heading')}
              </SettingsHead>
              <SettingsBody>
                <SettingsData>
                  <SettingLabelWrapper>
                    <SettingName>{t('pages.users.detail.settings.sign_in_count')}</SettingName>
                    <SettingDescription>
                      {t('pages.users.detail.settings.sign_in_count_description')}
                    </SettingDescription>
                  </SettingLabelWrapper>
                  <SettingInputWrapper>
                    <DetailTextField disabled fullWidth value={user?.signInCount ?? ''} />
                  </SettingInputWrapper>
                </SettingsData>

                <SettingsData>
                  <SettingLabelWrapper>
                    <SettingName>
                      {t('pages.users.detail.settings.current_sign_in_date')}
                    </SettingName>
                    <SettingDescription>
                      {t('pages.users.detail.settings.current_sign_in_date_description')}
                    </SettingDescription>
                  </SettingLabelWrapper>
                  <SettingInputWrapper>
                    <DetailTextField
                      disabled
                      fullWidth
                      value={formatDate(user?.currentSignInAt) ?? ''}
                    />
                  </SettingInputWrapper>
                </SettingsData>

                <SettingsData>
                  <SettingLabelWrapper>
                    <SettingName>{t('pages.users.detail.settings.current_sign_in_ip')}</SettingName>
                    <SettingDescription>
                      {t('pages.users.detail.settings.current_sign_in_ip_description')}
                    </SettingDescription>
                  </SettingLabelWrapper>
                  <SettingInputWrapper>
                    <DetailTextField disabled fullWidth value={user?.currentSignInIp ?? ''} />
                  </SettingInputWrapper>
                </SettingsData>

                <SettingsData>
                  <SettingLabelWrapper>
                    <SettingName>{t('pages.users.detail.settings.last_sign_in_date')}</SettingName>
                    <SettingDescription>
                      {t('pages.users.detail.settings.last_sign_in_date_description')}
                    </SettingDescription>
                  </SettingLabelWrapper>
                  <SettingInputWrapper>
                    <DetailTextField
                      disabled
                      fullWidth
                      value={formatDate(user?.lastSignInAt) ?? ''}
                    />
                  </SettingInputWrapper>
                </SettingsData>

                <SettingsData className="last">
                  <SettingLabelWrapper>
                    <SettingName>{t('pages.users.detail.settings.last_sign_in_ip')}</SettingName>
                    <SettingDescription>
                      {t('pages.users.detail.settings.last_sign_in_ip_description')}
                    </SettingDescription>
                  </SettingLabelWrapper>
                  <SettingInputWrapper>
                    <DetailTextField disabled fullWidth value={user?.lastSignInIp ?? ''} />
                  </SettingInputWrapper>
                </SettingsData>
              </SettingsBody>
            </div>
          </Box>

          <ConfirmDialog
            {...confirmProps}
            cancel={t('common.buttons.cancel')}
            confirm={t('common.dialogs.delete')}
            deleteConfirm
            prompt={t('pages.users.dialogs.delete_user.prompt')}
            title={t('pages.users.dialogs.delete_user.title')}
          />
        </PageContainer>
      </>
    </LoadingPane>
  );
};
